import { useCallback, useEffect, useState } from 'react';
import { applyBranding, type PublicTenant, type Trader } from '@ck/sdk';
import { api, remember, session, setSignedOutHandler, staffView } from './api';
import { Auth } from './Auth';
import { Careers } from './Careers';
import { LiveChat } from './LiveChat';
import { Shell } from './Shell';
import { PrefsProvider } from './prefs';
import { Logo, useFavicon } from '@ck/ui';

export function App() {
  const [tenant, setTenant] = useState<PublicTenant | null>(null);
  const [trader, setTrader] = useState<Trader | null>(null);
  const [booting, setBooting] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  useFavicon(tenant?.branding);

  const loadMe = useCallback(async () => {
    if (!session.get()) { setTrader(null); return; }
    try { const me = (await api.get<{ trader: Trader }>('/v1/me')).trader; setTrader(me); try { localStorage.setItem('ck.me', JSON.stringify(me)); } catch { /* ok */ } }
    catch { setTrader(null); try { localStorage.removeItem('ck.me'); } catch { /* ok */ } }
  }, []);

  useEffect(() => {
    setSignedOutHandler(() => { try { localStorage.removeItem('ck.me'); } catch { /* ok */ } setTrader(null); });
    // Keep the affiliate code from ?ref=CODE so sign-up can credit the partner, and count the click once.
    const ref = new URLSearchParams(location.search).get('ref');
    // Show the portal straight away from the last known branding and profile, then refresh in the background.
    try {
      const ct = JSON.parse(localStorage.getItem('ck.tenant') || 'null');
      if (ct?.branding) { setTenant(ct); applyBranding(ct.branding); if (!session.get()) setBooting(false); }
      const cm = session.get() && JSON.parse(localStorage.getItem('ck.me') || 'null');
      if (ct?.branding && cm?.id) { setTrader(cm); setBooting(false); }
    } catch { /* first visit */ }
    (async () => {
      try {
        const t = await api.tenant();
        try { localStorage.setItem('ck.tenant', JSON.stringify(t)); } catch { /* ok */ }
        setTenant(t);
        applyBranding(t.branding);
        document.title = `${t.branding.name} client portal`;
        if (ref && remember('ck.ref') !== ref.toUpperCase()) {
          remember('ck.ref', ref.toUpperCase());
          api.post(`/v1/ref/${encodeURIComponent(ref)}/click`).catch(() => undefined);
        }
        await loadMe();
      } catch (e) {
        let cached = false; try { cached = !!localStorage.getItem('ck.tenant'); } catch { /* ok */ }
        if (!cached) setFatal(e instanceof Error ? e.message : 'Something went wrong.');
      } finally { setBooting(false); }
    })();
  }, [loadMe]);

  // Market switches and country rules made in the CRM reach open portals within a minute.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return;
      api.tenant().then(setTenant).catch(() => undefined);
      if (session.get()) loadMe();
    }, 60_000);
    return () => clearInterval(t);
  }, [loadMe]);

  if (tenant?.maintenance?.on) return (
    <div className="maint-page"><Logo branding={tenant.branding} on="dark" height={40} /><h1>We’ll be right back</h1><p>{tenant.maintenance.message}</p><p>Your accounts and balances are safe.</p></div>
  );
  if (fatal) return <div className="fatal"><b>We couldn’t load the portal.</b><span>{fatal}</span><button className="btn" onClick={() => location.reload()}>Try again</button></div>;
  if (booting || !tenant) return <div className="boot" aria-busy="true"><span className="boot-mark">{tenant?.branding.mark ?? '·'}</span></div>;
  if (location.pathname.replace(/\/$/, '') === '/livechat') return <LiveChat tenant={tenant} />;
  if (location.hash.startsWith('#careers')) return <Careers tenant={tenant} />;
  if (!trader) return <PrefsProvider><Auth tenant={tenant} onSignedIn={(t) => setTrader(t)} /></PrefsProvider>;
  return <PrefsProvider>{staffView() && <div className="staff-view" role="status">Staff view of {trader.firstName} {trader.lastName}'s portal. Only you see this bar. Closes after an hour.<button onClick={async () => { await api.post('/v1/auth/logout').catch(() => undefined); session.set(null); window.close(); setTrader(null); }}>End staff view</button></div>}<Shell tenant={tenant} trader={trader} refreshTrader={loadMe} signOut={async () => {
    await api.post('/v1/auth/logout').catch(() => undefined);
    session.set(null);
    try { localStorage.removeItem('ck.me'); } catch { /* ok */ }
    setTrader(null);
  }} /></PrefsProvider>;
}
